// Responsive shell layout — sidebar on desktop, bottom nav on mobile.
// Mobile: single column with pb-16 for fixed bottom nav clearance.
// Desktop (md+): two-column grid [260px sidebar | 1fr main area].

import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Sidebar } from './Sidebar'
import { MobileNav } from './MobileNav'
import { BrandHeader } from './BrandHeader'
import { groups } from './navRegistry'
import { visibleGroups } from '@synvya/portal-ui/visibility'
import { useTeam } from '@/state/useTeam'
import { useServerProvisioningAutoHeal } from '@/hooks/useServerProvisioningAutoHeal'
import { useBillingLoader } from '@/hooks/useBillingLoader'
import { GrandfatherBanner } from '@/components/GrandfatherBanner'

/** Derive the section label from the active group based on current URL. */
function useActiveSectionLabel(): string {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const visible = visibleGroups(groups)

  // Longest-prefix match against group basePaths
  let best: (typeof visible)[0] | undefined
  for (const group of visible) {
    if (pathname.startsWith(group.basePath)) {
      if (!best || group.basePath.length > best.basePath.length) {
        best = group
      }
    }
  }

  return best ? t(`common:${best.label}`) : ''
}

export function AdminLayout() {
  const sectionLabel = useActiveSectionLabel()
  const { restaurantName, bunkerConnected, error: teamError } = useTeam()
  useServerProvisioningAutoHeal()
  useBillingLoader()

  // On a desktop the shell fills the window and its own regions scroll (the sidebar, the outline, the
  // detail pane). The page behind it must not scroll, or a pane that reaches its end drags the whole
  // shell up. The class is set only while this layout is mounted.
  useEffect(() => {
    const root = document.documentElement
    root.classList.add('shell-locked')
    return () => root.classList.remove('shell-locked')
  }, [])

  return (
    <div className="pb-16 md:grid md:h-dvh md:grid-cols-[auto_1fr] md:overflow-hidden md:pb-0">
      <Sidebar />
      {/* The workspace: the context bar, then one region that holds the page.
          The region scrolls for an ordinary page. A page that fills it (the
          menu editor) takes the remaining height through `flex-1 min-h-0`. */}
      <main className="flex min-h-screen min-w-0 flex-col md:min-h-0 md:overflow-hidden">
        <BrandHeader
          restaurantName={restaurantName ?? 'My Restaurant'}
          sectionLabel={sectionLabel}
          bunkerConnected={bunkerConnected}
          connectionError={teamError}
        />
        <div className="flex flex-1 flex-col md:min-h-0 md:overflow-auto">
          {/* The reason the pill reads OFFLINE. It used to be a `title`
              tooltip on that pill, which a touch device never shows and a
              merchant never hovers — so a restaurant that could not sign
              looked the same as one that could. */}
          {teamError && !bunkerConnected && (
            <div
              role="alert"
              className="shrink-0 border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive"
            >
              {teamError}
            </div>
          )}
          <GrandfatherBanner />
          <div className="mx-auto w-full flex-1 md:min-h-0 2xl:max-w-screen-2xl">
            <Outlet />
          </div>
        </div>
      </main>
      <MobileNav />
    </div>
  )
}
